import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AccountContext } from "../contexts/AccountContexts"
import { PermissionContext } from "../contexts/PermissionContexts"
import { ContentContext } from "../contexts/ContentContext"
import { useConfirm } from "../contexts/ModalContexts"
import { loadingActive } from "./Background"
import { requestSetDefaultPriorityLevel } from "../requests/setup_requests"
import { StyleSelectScreen } from "../utils/setupUtils"
import { darkAndLightThemeDefault } from "./ThemeComponents"
import { languageOptions } from "../entries"
import { GoPlus } from "react-icons/go"


export const CategorySetupElementEdit = ({ props, setOpen, setElement, edit, hasDefault = false, isDeletable = true, styles, requestNewElement, requestEditElement, requestDeleteElement }) => {
    const { t } = useTranslation('settings')
    const { user, language } = useContext(AccountContext)
    const { hasPermission } = useContext(PermissionContext)
    const { openErrorModal } = useContext(ContentContext)
    const confirm = useConfirm()
    const [title, setTitle] = useState(props.title ? props.title : "")
    const [value, setValue] = useState(props.value ? props.value : 0)
    const [style, setStyle] = useState(props.style ? props.style : "")
    const [locales, setLocales] = useState(props?.locales ?? {})
    const [hasViolaterForm, setHasViolaterForm] = useState(props?.has_violaters_form ?? false)
    const [isDefault, setIsDefault] = useState(props.default ? props.default : false)

    const handleSubmit = () => {
        loadingActive(true)
        if (!edit) {

            requestNewElement({ userId: user.id, title: title, value: value, style: style, locales: locales, hasViolatersForm: hasViolaterForm })
                .then((data) => {
                    setElement(data)
                    setOpen(false)
                    loadingActive(false)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false);

                })
        }
        else {
            if (hasDefault && !props.default && isDefault) {
                requestSetDefaultPriorityLevel({ id: props.id })
                    .then((data) => {
                        setElement(data)
                    })
                    .catch((error) => {
                        console.error(error.status, error.message)
                    })
            }
            requestEditElement({ userId: user.id, id: props.id, title: title, value: value, style: style, locales: locales, hasViolatersForm: hasViolaterForm })
                .then((data) => {
                    setElement(data)
                    setOpen(false)
                    loadingActive(false)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false);

                })
        }
    }

    const toggleDefault = () => setIsDefault(!isDefault)

    const handleDelete = async () => {
        const isConfirmed = await confirm(t("Are you sure you want to delete this item? This action cannot be undone."))
        if (isConfirmed) {

            loadingActive(true)
            requestDeleteElement({ userId: user.id, id: props.id })
                .then((data) => {
                    setOpen((prev) => false)
                    setElement(data)
                    loadingActive(false)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                    loadingActive(false);
                })
        }
    }

    const handleClose = () => {
        setOpen(false)
    }

    const onChangeLocale = (locale, val) => {
        setLocales((prev) => ({ ...prev, [locale]: val }))
    }


    return (
        <div className="flex flex-col w-full gap-2">

            <div className="grid grid-cols-5 w-full gap-3 items-center">
                <label className="form-control w-full">
                    <input
                        type="text"
                        placeholder={t('Title')}
                        className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value);
                            onChangeLocale(language, e.target.value)
                        }}
                    />
                </label>
                <label className="form-control w-full">

                    <input
                        type="number"
                        data-theme="light"
                        placeholder={t('Value')}
                        className="input input-bordered  input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                        required={true}
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value);
                        }}
                    />
                </label>

                {
                    hasDefault && hasPermission("priority:set_default") &&
                    <div className="form-control">
                        <label className="label cursor-pointer gap-2 justify-center">
                            <span className="label-text">{t('Set as default')}</span>
                            {/* <input type="checkbox" className="toggle checked:bg-green-600" checked={isDefault} onChange={toggleDefault} /> */}
                            <input type="radio" name="radio-1" className="radio checked:bg-green-600" checked={isDefault} onChange={toggleDefault} />
                        </label>
                    </div>
                }

                <StyleSelectScreen styles={styles} target={title} props={props} getData={setStyle} />
                <div className="flex gap-3 mt-auto ltr:ml-auto rtl:mr-auto">

                    <button className="btn bg-green-600 hover:bg-green-500 border-none text-white" onClick={handleSubmit}>{t('Submit')}</button>
                    {edit
                        &&
                        <button className="btn bg-red-600 hover:bg-red-500 border-none text-white" disabled={(hasDefault && props.default) || !isDeletable} onClick={handleDelete}>{t('Delete')}</button>
                    }
                    <button className={`btn ${darkAndLightThemeDefault}`} onClick={handleClose}>{t('Cancel')}</button>
                </div>
            </div>

            <div className="flex gap-10 items-start">

                <div className="flex flex-col gap-1 max-w-72">
                    {
                        Object.entries(languageOptions).map(([lang, v], index) =>
                            <div key={lang + index} className="flex gap-2 items-center">
                                <div className="w-8 max-w-8" title={v?.name}>
                                    <img src={v?.icon} />
                                </div>

                                <label className="form-control w-full max-w-56">
                                    <input
                                        type="text"
                                        placeholder={t('Title')}
                                        className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
                                        value={locales?.[lang]}
                                        onChange={(e) => {
                                            onChangeLocale(lang, e.target.value)
                                        }}
                                    />
                                </label>
                            </div>
                        )
                    }
                </div>

                <div className="flex flex-col gap-1 py-3">
                    <div className="flex gap-2 items-center">
                        <input type="checkbox" checked={hasViolaterForm} onChange={(e) => setHasViolaterForm(e.target.checked)} className="checkbox checkbox-sm  bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" />
                        <span>{t("add_violaters_name_form")}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}